import React from 'react';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { useUser } from '../../../../hooks/useUser';
import { useI18nContext } from '../../../I18nProvider/I18nProvider';
import { getSelectedLanguage } from '../../../../utilities/localization';
import { IDP_TEST_LANGUAGE_PARAM } from './idpTestResult';

/**
 * The result page: a separate webpack entry (app/idpTestCallback.tsx) outside
 * the app's router, so it's reached by its file name. A suffix-less path would
 * fall back to index.html (CloudFront 403/404, dev server historyApiFallback),
 * i.e. the app with its auth.
 */
export const IDP_TEST_CALLBACK_PATH = '/idp-test-callback.html';

interface IdpTestLinkInput {
    auth0Domain?: string;
    clientId?: string;
    connectionName?: string;
    /** Sent to the result page in the redirect_uri query. */
    displayName?: string;
    /** Sent to the result page in the redirect_uri query. */
    language?: string;
    origin: string;
    random?: () => string;
}

const randomToken = (): string => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Auth0 /authorize URL for a test sign-in with one connection.
 *
 * - `bb_idp_test=1`: the post-login Action denies the login and puts the result
 *   in the deny, so no token is ever issued.
 * - `connection` skips the bb-sign-in-ui picker.
 * - `prompt=login` and `max_age=0` force a fresh IdP sign-in.
 */
export const buildIdpTestLink = ({
    auth0Domain,
    clientId,
    connectionName,
    displayName,
    language,
    origin,
    random = randomToken,
}: IdpTestLinkInput): string | undefined => {
    if (!auth0Domain || !clientId || !connectionName) return undefined;

    // %20 for spaces (URLSearchParams would use "+"); this form is verified with Auth0.
    const resultQuery = [
        displayName && `idp=${encodeURIComponent(displayName)}`,
        language &&
            `${IDP_TEST_LANGUAGE_PARAM}=${encodeURIComponent(language)}`,
    ]
        .filter(Boolean)
        .join('&');
    const redirectUri = `${origin}${IDP_TEST_CALLBACK_PATH}${
        resultQuery ? `?${resultQuery}` : ''
    }`;

    const params = new URLSearchParams({
        client_id: clientId,
        bb_idp_test: '1',
        connection: connectionName,
        prompt: 'login',
        max_age: '0',
        response_type: 'code',
        // No response_mode=fragment: Auth0 then shows its own error page for
        // the deny instead of redirecting.
        scope: 'openid profile email',
        redirect_uri: redirectUri,
        // Required by /authorize; not verified (no code exchange).
        state: random(),
        nonce: random(),
    });

    return `https://${auth0Domain}/authorize?${params.toString()}`;
};

/** The test link for the admin's region (`uas.config.auth0Clients`) and language. */
export const useIdpTestLink = (
    connectionName: string | undefined,
    displayName: string | undefined,
): string | undefined => {
    const { uas } = useAppConfigContext();
    const user = useUser();
    const region = user?.region;
    const client = region ? uas?.config?.auth0Clients?.[region] : undefined;
    const domain = client?.domain;
    const clientId = client?.client_id;
    const { userSelectedLang } = useI18nContext();
    const language = getSelectedLanguage(userSelectedLang);

    // Memoized: the shown and the copied link must match (random state).
    return React.useMemo(
        () =>
            buildIdpTestLink({
                auth0Domain: domain,
                clientId,
                connectionName,
                displayName,
                language,
                origin: window.location.origin,
            }),
        [domain, clientId, connectionName, displayName, language],
    );
};
